import { useState } from 'react';
import {
  Check,
  Copy,
  Download,
  Gift,
  MailCheck,
  Printer,
  TriangleAlert,
} from 'lucide-react';
import { GeneratedGiftcard } from '../../../interfaces/giftcard.interface';
import { downloadGiftcard, printGiftcard } from '../utils/printGiftcard';

const formatDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '—';

interface Props {
  giftcard: GeneratedGiftcard | null;
  emailRequested: boolean;
}

const GiftcardResult = ({ giftcard, emailRequested }: Props) => {
  const [copied, setCopied] = useState(false);

  if (!giftcard) {
    return (
      <div className="h-full min-h-[420px] flex flex-col items-center justify-center text-center rounded-2xl border-2 border-dashed border-gray-200 bg-white/50 p-8">
        <Gift size={40} className="text-[#623413]/30 mb-4" />
        <h3 className="text-lg font-semibold text-gray-700">
          Aucun bon généré pour l'instant
        </h3>
        <p className="mt-2 max-w-sm text-sm text-gray-500">
          Remplissez le formulaire pour créer un bon cadeau réglé en boutique.
          Il apparaîtra ici, prêt à imprimer, et sera immédiatement utilisable
          sur le site avec son code.
        </p>
      </div>
    );
  }

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(giftcard.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard refusé (contexte non sécurisé) : le code reste lisible à l'écran.
    }
  };

  return (
    <div className="rounded-2xl border border-[#623413]/10 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 border-b border-gray-100 bg-[#623413]/5 px-6 py-4">
        <Check size={18} className="text-green-600" />
        <h3 className="font-semibold text-[#623413]">
          Bon de {giftcard.value} € généré
        </h3>
      </div>

      <div className="p-6 space-y-5">
        {giftcard.url ? (
          <img
            src={giftcard.url}
            alt={`Bon cadeau ${giftcard.code}`}
            className="w-full rounded-xl border border-gray-100 shadow-sm"
          />
        ) : (
          <p className="text-sm text-gray-500">
            Aperçu de l'image indisponible.
          </p>
        )}

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Code</p>
            <button
              type="button"
              onClick={copyCode}
              className="mt-1 inline-flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 font-mono text-lg font-semibold tracking-widest text-[#623413] hover:bg-gray-100 transition"
              title="Copier le code"
            >
              {giftcard.code}
              {copied ? (
                <Check size={16} className="text-green-600" />
              ) : (
                <Copy size={16} className="text-gray-400" />
              )}
            </button>
          </div>
          <div>
            <p className="text-gray-500">Valable jusqu'au</p>
            <p className="mt-1 py-2 font-medium text-gray-800">
              {formatDate(giftcard.expiresAt)}
            </p>
          </div>
        </div>

        {/* Sur un rejeu idempotent, le backend renvoie emailSent:false sans pouvoir
            savoir si l'appel gagnant a envoyé l'e-mail : on n'affiche pas de fausse
            alerte, le bandeau « déjà généré » ci-dessous informe déjà l'utilisateur. */}
        {emailRequested &&
          !giftcard.alreadyExisted &&
          (giftcard.emailSent ? (
            <p className="flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
              <MailCheck size={16} />
              Bon envoyé par e-mail au client.
            </p>
          ) : (
            <p className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
              <TriangleAlert size={16} />
              Le bon est valide, mais l'e-mail n'a pas pu être envoyé.
              Imprimez-le ou téléchargez-le.
            </p>
          ))}

        {giftcard.alreadyExisted && (
          <p className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700">
            Ce bon avait déjà été généré : aucun doublon n'a été créé.
          </p>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <button
            type="button"
            disabled={!giftcard.url}
            onClick={() => giftcard.url && printGiftcard(giftcard.url)}
            className="flex-1 min-w-[140px] flex items-center justify-center gap-2 rounded-lg bg-[#623413] px-4 py-2.5 text-white font-medium hover:opacity-90 disabled:opacity-50 transition"
          >
            <Printer size={18} />
            Imprimer
          </button>
          <button
            type="button"
            disabled={!giftcard.url}
            onClick={() =>
              giftcard.url && downloadGiftcard(giftcard.url, giftcard.code)
            }
            className="flex-1 min-w-[140px] flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 transition"
          >
            <Download size={18} />
            Télécharger
          </button>
        </div>
      </div>
    </div>
  );
};

export default GiftcardResult;
